import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetAtom } from 'jotai';
import { Box } from '@mui/material';

import { RankDialogUser, rankDialogAtom } from '../../atoms';
import { RANK_COLORS, isRank } from '../../helpers';
import styles from './style';

type RankBadgeSize = 'small' | 'medium';

interface RankBadgeProps {
  // recruit, soldier, veteran, elite, legend
  rank?: string;
  size?: RankBadgeSize;
  // Nothing for a recruit (the comments and the lists of players, like the app).
  hideRecruit?: boolean;
  // When given, a click explains the ranks for this user.
  user?: RankDialogUser;
}

// Rank of a player, as in the app: a pill in the color of the rank.
const RankBadge: React.FC<RankBadgeProps> = React.memo((props) => {
  const {
    rank,
    size = 'small',
    hideRecruit,
    user,
  } = props;
  const { t } = useTranslation();
  const setRankDialog = useSetAtom(rankDialogAtom);

  const color = isRank(rank) ? RANK_COLORS[rank] : '';

  const badgeSx = useMemo(() => ({
    ...(size === 'medium' ? styles.medium : styles.small),
    ...(user ? styles.clickable : {}),
    color,
    borderColor: `${color}73`,
    backgroundColor: `${color}24`,
  }), [color, size, user]);

  // The badge is often in a clickable row (the username of a video): only the dialog opens.
  const onClick = useCallback((event: React.MouseEvent) => {
    if (user) {
      event.stopPropagation();
      setRankDialog(user);
    }
  }, [setRankDialog, user]);

  if (!color || (hideRecruit && rank === 'recruit')) {
    return null;
  }

  if (user) {
    return (
      <Box component="button" type="button" onClick={onClick} sx={badgeSx}>
        {t(`admin.ranks.${rank}`)}
      </Box>
    );
  }

  return <Box component="span" sx={badgeSx}>{t(`admin.ranks.${rank}`)}</Box>;
});

export type { RankBadgeSize };

export default RankBadge;
